import React, { useContext, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, Flex, Icon, InterfaceErrorIcon, Text } from '@grammarly/design-system'

import { gnarSpec } from '@lib/tracking/gnar'
import { PageContext } from '@contexts/page-context'
import { UploadButton } from './upload-button'
import { analyzeTextAndMaybeTruncateToDesiredWordLimit } from '../utils'
import { useTool } from '../tool-context'
import { ToolFormValues } from '../types'
import { ToolErrors } from '../constants'
import { getListOfFileTypesCopy } from './upload-button/utils'

import styles from '../tool.module.scss'

export const ToolActionRow: React.FC = () => {
  const { toolInputName, isLoading, submitButtonText, canUploadFile, setToolError, sampleText } = useTool()

  const { pageId, slug } = useContext(PageContext)

  const { watch, setValue, clearErrors } = useFormContext<ToolFormValues>()
  const text = watch(toolInputName)

  const wordCount = analyzeTextAndMaybeTruncateToDesiredWordLimit(text).words

  return (
    <Flex
      direction="row"
      marginTop="auto"
      paddingTop={5}
      paddingBottom={5}
      paddingLeft={4}
      paddingRight={4}
      align="center"
      justify="space-between"
      gap={2}
      wrap
    >
      <Flex direction="row" align="center" gap={2}>
        <Button type="submit" isLoading={isLoading} isDisabled={isLoading}>
          <span style={{ opacity: isLoading ? 0 : 1 }}>{submitButtonText}</span>
        </Button>
        {canUploadFile && (
          <UploadButton
            onError={(error, _fileName, maxFileSize, userFileSize) => {
              switch (error) {
                case 'FILE_ERROR_INVALID_EXT':
                  setToolError(`File type not supported. Please upload a ${getListOfFileTypesCopy('or')}.`)
                  break
                case 'FILE_ERROR_TOO_BIG': {
                  if (typeof maxFileSize === 'number' && typeof userFileSize === 'number') {
                    const maxFileSizeMB = (maxFileSize / 1000000).toFixed(2)
                    const userFileSizeMB = (userFileSize / 1000000).toFixed(2)
                    setToolError(
                      `The file size of ${userFileSizeMB} MB exceeds the ${maxFileSizeMB} MB limit. Please upload a smaller file.`
                    )
                  } else {
                    setToolError('The file uploaded is too large. Please upload a smaller file.')
                  }
                  break
                }
                case 'UNKNOWN_ERROR':
                default:
                  setToolError('There was an error uploading your file.')
                  break
              }

              console.error(error)
              gnarSpec.toolFileUploadFailure(error, pageId, slug, userFileSize)
            }}
            text="Upload file"
            isDisabled={isLoading}
            isLoading={isLoading}
            onFileChangeFinish={(text, fileInfo) => {
              setValue(toolInputName, text)
              gnarSpec.toolFileUploadSuccess(pageId, slug, fileInfo.size, fileInfo.type)
            }}
            onClick={() => {
              setToolError(null)
              clearErrors(toolInputName)
              gnarSpec.toolFileUploadButtonClick(pageId, slug)
            }}
          />
        )}
        {sampleText && (
          <Button
            variant="tertiary"
            isDisabled={isLoading}
            onClick={() => {
              setValue(toolInputName, sampleText)
              gnarSpec.toolSampleTextClick(pageId, slug)
            }}
          >
            Try sample text
          </Button>
        )}
      </Flex>
      <WordCounter wordCount={wordCount} />
    </Flex>
  )
}

interface WordCounterProps {
  wordCount: number
}

const WordCounter: React.FC<WordCounterProps> = ({ wordCount }) => {
  const { toolInputName, minWordLimit, maxWordLimit } = useTool()
  const { subscribe } = useFormContext<ToolFormValues>()
  const [inputError, setInputError] = useState<React.ReactNode | null>(null)

  useEffect(() => {
    const errorSubscription = subscribe({
      formState: {
        errors: true,
      },
      callback: ({ errors }) => {
        if (errors) {
          const errorMessage = errors[toolInputName]?.message
          if (errorMessage) {
            setInputError(errorMessage)
          } else {
            setInputError(null)
          }
        }
      },
    })
    return () => errorSubscription()
  }, [minWordLimit, subscribe, toolInputName, wordCount])

  if (inputError) {
    const errorMessage =
      inputError === ToolErrors.MIN_WORD_LIMIT_ERROR
        ? `Enter ${minWordLimit - wordCount} more words`
        : 'Please enter text'

    return (
      <Flex direction="row" align="center" justify="center" gap={1}>
        <Icon icon={InterfaceErrorIcon} accessibilityLabel="" />
        <Text variant="text-xsmall" as="span" color="critical-default" className={styles.toolActionRowErrorCopy}>
          {errorMessage}
        </Text>
      </Flex>
    )
  }

  if (wordCount < minWordLimit) {
    const wordsLeft = minWordLimit - wordCount

    return (
      <Flex direction="row" align="center" gap={1}>
        <Text variant="text-xsmall" as="span">
          Enter {wordsLeft} more words
        </Text>
      </Flex>
    )
  }

  const wordLimitText = `${wordCount}/${maxWordLimit} words`

  return (
    <Flex direction="row" align="center" gap={1}>
      <Text variant="text-xsmall" as="span">
        {wordLimitText}
      </Text>
    </Flex>
  )
}
